// ----------------------------------------------------------------------------
// Tips content. Themed collections of short, action focused articles that
// match the redesigned admin: four tabs across the top (Home, Songs, Roster,
// Setlists), a section bar under them, the Create button, the bell, the help
// button and the account menu. Name controls exactly as they appear on screen.
// Keep all user facing copy free of hyphens.
// ----------------------------------------------------------------------------

export interface TipStep {
  title: string;
  detail: string;
}

export interface TipArticle {
  id: string;
  title: string;
  summary: string;
  steps: TipStep[];
  /** Optional deep link button to the live admin page */
  tryIt?: { label: string; path: string };
}

export interface TipCollection {
  id: string;
  title: string;
  description: string;
  articles: TipArticle[];
}

export const TIP_COLLECTIONS: TipCollection[] = [
  {
    id: 'essentials',
    title: 'Essentials',
    description: 'Find your way around and pick the team you are working on',
    articles: [
      {
        id: 'find-your-way',
        title: 'Find your way around',
        summary: 'Four tabs, a section bar, and everything else in the top bar.',
        steps: [
          { title: 'Use the four tabs', detail: 'Home, Songs, Roster and Setlists sit in the middle of the top bar. On a phone they move to a row under it.' },
          { title: 'Switch sections', detail: 'Some pages have a bar of sections under the tabs. Songs has Library and Song Studio. Roster has Roster and Availability.' },
          { title: 'Open everything else from your initials', detail: 'The account menu on the right holds Profile, Church settings, Teams and people, Plan and billing, Notifications, Tips and support and Developer.' },
          { title: 'Create from anywhere', detail: 'The red Create button starts a new service plan, adds a song, starts a setlist, makes an invite code or uploads to Song Studio.' },
        ],
        tryIt: { label: 'Go home', path: '/admin/dashboard' },
      },
      {
        id: 'switch-team',
        title: 'Work on one team at a time',
        summary: 'Each worship team has its own roster, setlists and availability.',
        steps: [
          { title: 'Check the team pill', detail: 'The pill next to the logo shows the team you are working on. Home, Roster, Setlists and Availability show that team only.' },
          { title: 'Switch teams', detail: 'Click the pill and pick another team. Songs stay shared across the whole church.' },
          { title: 'Start a new team', detail: 'Choose New worship team at the bottom of the same list.' },
        ],
      },
      {
        id: 'understand-roles',
        title: 'Understand roles',
        summary: 'What Admins, Members, Singers and Musicians can do.',
        steps: [
          { title: 'Admins run the church', detail: 'Admins see every team, roster, setlist and member, and manage the plan and billing.' },
          { title: 'Members, Singers and Musicians serve', detail: 'They see the services they are scheduled for, respond to them and see those setlists in the member app.' },
          { title: 'Change a role', detail: 'Open People, then Members, and choose Edit next to the person.' },
        ],
        tryIt: { label: 'Open Members', path: '/admin/members' },
      },
      {
        id: 'light-and-dark',
        title: 'Switch between light and dark',
        summary: 'Pick the look that suits you.',
        steps: [
          { title: 'Use the moon or sun button', detail: 'It sits in the top bar next to Create. Your choice is remembered on this device.' },
        ],
      },
    ],
  },
  {
    id: 'teams',
    title: 'Teams and people',
    description: 'Create teams, invite people with a code and approve them',
    articles: [
      {
        id: 'create-team',
        title: 'Create a worship team',
        summary: 'Give a group its own roster, setlists and availability.',
        steps: [
          { title: 'Open Teams', detail: 'From the account menu choose Teams and people. Teams is the first section.' },
          { title: 'Choose New team', detail: 'Name the team, pick a leader and say what it serves, like Friday youth nights.' },
          { title: 'Set a size limit if you need one', detail: 'Leave Team size limit blank for no limit. When a team is full, no one else can join it until you raise the limit or clear it.' },
          { title: 'Pick how setlists are built', detail: 'On the team card, Setlists by default is either Worship leader builds or Admin predefined. You can change it for a single service on Roster.' },
        ],
        tryIt: { label: 'Open Teams', path: '/admin/team' },
      },
      {
        id: 'invite-with-code',
        title: 'Invite people with a code',
        summary: 'Everyone joins with a team invite code that you share however you like.',
        steps: [
          { title: 'Open Invite codes', detail: 'In Teams and people, choose the Invite codes section.' },
          { title: 'Generate a code', detail: 'Pick the team they will join and how long the code lasts: 1 day, 7 days or 30 days. Then choose Generate code.' },
          { title: 'Share it', detail: 'Choose Copy and send the code however you like. People enter it after they sign up.' },
          { title: 'Replace or stop a code', detail: 'Regenerate gives the team a new code. Revoke stops the current one from working.' },
        ],
        tryIt: { label: 'Open Invite codes', path: '/admin/invite' },
      },
      {
        id: 'approve-requests',
        title: 'Approve join requests',
        summary: 'People who use a code wait for you to let them in.',
        steps: [
          { title: 'Watch for the badge', detail: 'The Requests section shows how many people are waiting, and the bell lights up.' },
          { title: 'Approve or decline', detail: 'Approved people join as Members. Declined requests are removed and can try again with a new code.' },
          { title: 'If their team is full', detail: 'They are still approved, and you are told the team was full. Raise its size limit on Teams, then add them from Members.' },
        ],
        tryIt: { label: 'Open Requests', path: '/admin/membership-requests' },
      },
      {
        id: 'edit-member',
        title: 'Change someone’s role or teams',
        summary: 'Keep roles and team membership up to date.',
        steps: [
          { title: 'Open Members', detail: 'In Teams and people, choose Members. Search by name, email or role.' },
          { title: 'Choose Edit', detail: 'Pick a role and turn worship teams on or off for that person, then Save.' },
          { title: 'Remove someone', detail: 'Choose Remove in the same window. Past rosters keep their record.' },
        ],
        tryIt: { label: 'Open Members', path: '/admin/members' },
      },
    ],
  },
  {
    id: 'roster',
    title: 'Roster and services',
    description: 'Plan services, schedule people and keep the team in sync',
    articles: [
      {
        id: 'plan-service',
        title: 'Plan a service',
        summary: 'Four short steps from date to published plan.',
        steps: [
          { title: 'Start a new plan', detail: 'Choose Create, then New service plan. You can also use New service plan on the Roster page.' },
          { title: 'When', detail: 'Pick the team, a name, the date and the start time. Add notes for the team if you like.' },
          { title: 'Who', detail: 'Start from the last service or an empty team, then set a person for each position.' },
          { title: 'Setlist and review', detail: 'Add songs from your library or skip the setlist for now. Then Save draft or Publish.' },
        ],
        tryIt: { label: 'Plan a service', path: '/admin/roster?plan=new' },
      },
      {
        id: 'schedule-people',
        title: 'Schedule people on a service',
        summary: 'Add, change and confirm the people serving.',
        steps: [
          { title: 'Pick the service', detail: 'On Roster, choose the service in the month view, or switch to List to see every service.' },
          { title: 'Add someone', detail: 'Choose Add person, pick the person and their position, then Add to service. People who are on leave or away that day can’t be picked.' },
          { title: 'Add a guest', detail: 'For someone who hasn’t joined the app yet, choose Add a guest by name.' },
          { title: 'Change a response', detail: 'Click the status next to a name to mark them pending, confirmed or declined.' },
        ],
        tryIt: { label: 'Open Roster', path: '/admin/roster' },
      },
      {
        id: 'publish-roster',
        title: 'Publish a roster',
        summary: 'Let everyone on the service know and ask them to respond.',
        steps: [
          { title: 'Save as you go', detail: 'Save Draft keeps your changes without telling anyone.' },
          { title: 'Publish', detail: 'Choose Publish roster. Everyone on it gets an email and a push notification asking them to accept or decline.' },
          { title: 'Republish after changes', detail: 'If you change a published roster, choose Republish so the team sees the update.' },
        ],
        tryIt: { label: 'Open Roster', path: '/admin/roster' },
      },
      {
        id: 'reschedule-service',
        title: 'Move a service to another date',
        summary: 'Reschedule without rebuilding the plan.',
        steps: [
          { title: 'Open the service menu', detail: 'On the selected service, choose the button with three dots, then Reschedule.' },
          { title: 'Pick the new date and time', detail: 'Upcoming services move as they are and everyone is asked to confirm again.' },
          { title: 'Past services are copied', detail: 'A service that already happened stays as history, and a new one is made for the new date with the same songs and team.' },
        ],
        tryIt: { label: 'Open Roster', path: '/admin/roster' },
      },
      {
        id: 'availability',
        title: 'Set availability for a member',
        summary: 'Mark days someone can’t serve, or put them on leave.',
        steps: [
          { title: 'Open Availability', detail: 'On the Roster tab, choose the Availability section. Each row is a person and each square is a day.' },
          { title: 'Change a day', detail: 'Click a day to go from available to unavailable to tentative. The member can still change it themselves.' },
          { title: 'Set leave', detail: 'Choose Set leave for someone who is away for a while. They can’t be scheduled until you choose End leave.' },
        ],
        tryIt: { label: 'Open Availability', path: '/admin/team-availability' },
      },
    ],
  },
  {
    id: 'setlists',
    title: 'Setlists',
    description: 'Build setlists, send lyrics and keep a history',
    articles: [
      {
        id: 'build-setlist',
        title: 'Build and publish a setlist',
        summary: 'For teams where an admin chooses the songs.',
        steps: [
          { title: 'Check the setlist mode', detail: 'On the selected service, Setlist mode for this service shows who builds it. Admin predefined means you do.' },
          { title: 'Choose Build setlist', detail: 'Search your song library, add songs, then set the section, key, notes and lyrics for each one.' },
          { title: 'Publish the setlist', detail: 'Choose Publish setlist. The Team and Setlist switch then appears on that service.' },
          { title: 'Worship leader builds', detail: 'In this mode the team’s leader adds the songs and publishes from the member app.' },
        ],
        tryIt: { label: 'Open Roster', path: '/admin/roster' },
      },
      {
        id: 'lyrics-email',
        title: 'Email lyrics to your media team',
        summary: 'Send the words in setlist order when a setlist is published.',
        steps: [
          { title: 'Open Church settings', detail: 'From the account menu choose Church settings, then go to Setlist emails.' },
          { title: 'Turn it on', detail: 'Switch on Send lyrics when a setlist is published and add one or more email addresses.' },
          { title: 'Choose what happens to songs without lyrics', detail: 'Ask first warns the leader, Require lyrics blocks publishing, and Don’t check skips the check. Then choose Save changes.' },
          { title: 'Send it again', detail: 'On a published service, open the service menu and choose Resend lyrics email.' },
        ],
        tryIt: { label: 'Open Church settings', path: '/admin/settings' },
      },
      {
        id: 'setlist-history',
        title: 'Look back at past setlists',
        summary: 'Every setlist that was played stays in History.',
        steps: [
          { title: 'Open Setlists', detail: 'Choose the Setlists tab, then History.' },
          { title: 'Clear an old setlist', detail: 'Past setlists are read only. Delete clears the songs from that service; upcoming setlists can’t be deleted.' },
          { title: 'Fix setlists with no team', detail: 'If a team was deleted, a banner offers to move its setlists to another team.' },
        ],
        tryIt: { label: 'Open Setlists', path: '/admin/setlists?tab=history' },
      },
    ],
  },
  {
    id: 'songs',
    title: 'Song library',
    description: 'Keep the songs your team can pull from',
    articles: [
      {
        id: 'add-songs',
        title: 'Add songs to your library',
        summary: 'Title, artist, key and tempo so leaders can pick fast.',
        steps: [
          { title: 'Choose Add song', detail: 'On the Songs tab choose Add song, or use Create and then Add song.' },
          { title: 'Fill in the basics', detail: 'Add the title, artist, key and BPM. A YouTube link and CCLI number are optional.' },
          { title: 'Open a song', detail: 'Click a song to see its details, how often it has been sung and the setlists it was in.' },
        ],
        tryIt: { label: 'Open Songs', path: '/admin/songs' },
      },
      {
        id: 'import-songs',
        title: 'Import a list of songs',
        summary: 'Bring in a list you already have.',
        steps: [
          { title: 'Choose Import CSV', detail: 'On the Songs tab, choose Import CSV and pick your file with title, author, key, tempo and CCLI number.' },
          { title: 'Available on Basic and up', detail: 'On the Free plan the button shows a lock and takes you to plans.' },
          { title: 'Keep play counts current', detail: 'Sync play counts recounts how often each song has been sung from your past services.' },
        ],
        tryIt: { label: 'Open Songs', path: '/admin/songs' },
      },
    ],
  },
  {
    id: 'song-studio',
    title: 'Song Studio',
    description: 'Turn a rehearsal recording into chords and stems',
    articles: [
      {
        id: 'song-studio-how',
        title: 'How Song Studio works',
        summary: 'Upload a recording and get its key, tempo and chords.',
        steps: [
          { title: 'Open Song Studio', detail: 'On the Songs tab choose the Song Studio section, or use Create and then Upload to Song Studio.' },
          { title: 'Choose a file', detail: 'Drop a recording on the page or choose Choose file from computer. MP3, WAV and M4A files work.' },
          { title: 'Add the details', detail: 'Give it a title, or leave it blank to use the file name. Add the artist, rehearsal date and a key hint if you know it.' },
          { title: 'Let it analyze', detail: 'You can leave the page while it works. You get a notification when it is ready.' },
          { title: 'Any member can upload', detail: 'Members upload from the Song Studio page in their app. Song Studio songs are kept separate from the Song library.' },
        ],
        tryIt: { label: 'Open Song Studio', path: '/admin/chord-library' },
      },
      {
        id: 'song-studio-library',
        title: 'Manage your 20 songs',
        summary: 'Each church keeps up to 20 songs in Song Studio.',
        steps: [
          { title: 'Replace when full', detail: 'At 20 songs, uploading asks which song to replace. The oldest one is picked for you.' },
          { title: 'Rename', detail: 'Use the pencil next to a song to change its title.' },
          { title: 'Remove', detail: 'Remove deletes the recording, its stems and its chords after you confirm, and frees a slot.' },
        ],
        tryIt: { label: 'Open Song Studio', path: '/admin/chord-library' },
      },
      {
        id: 'song-studio-plans',
        title: 'What each plan includes',
        summary: 'Which plans get chords and which get stems.',
        steps: [
          { title: 'Chords on Basic and up', detail: 'Basic, Pro, Founders and Enterprise detect the key, tempo and chords.' },
          { title: 'Stems on Pro and up', detail: 'Pro, Founders and Enterprise also split each song into vocals, drums, bass, guitar, piano and other.' },
          { title: 'Free plan', detail: 'Song Studio isn’t included on Free.' },
        ],
        tryIt: { label: 'See plans', path: '/admin/subscription' },
      },
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Stay on top of requests and reach your church',
    articles: [
      {
        id: 'inbox',
        title: 'Check what needs you',
        summary: 'Requests, responses and availability changes in one place.',
        steps: [
          { title: 'Open the bell', detail: 'A red dot means something new. Items are grouped by Today and This week.' },
          { title: 'Go to the source', detail: 'Click an item to open it: a request opens Requests, a response opens that service on Roster.' },
          { title: 'Clear the dot', detail: 'Choose Mark all read.' },
        ],
        tryIt: { label: 'Open Notifications', path: '/admin/notifications' },
      },
      {
        id: 'broadcast',
        title: 'Send a push broadcast',
        summary: 'Send a short message to phones across your church.',
        steps: [
          { title: 'Choose New broadcast', detail: 'It’s at the top of Notifications.' },
          { title: 'Write it', detail: 'Start from a suggestion or write your own. Pick the whole church or specific people, then add a title and message.' },
          { title: 'Review, then send', detail: 'Review shows how many people and devices will get it. Choose Send now to deliver it.' },
        ],
        tryIt: { label: 'Open Notifications', path: '/admin/notifications' },
      },
    ],
  },
  {
    id: 'account',
    title: 'Church and account',
    description: 'Church details, your profile and integrations',
    articles: [
      {
        id: 'church-settings',
        title: 'Update church details',
        summary: 'Name, contact details, address and email preferences.',
        steps: [
          { title: 'Open Church settings', detail: 'From the account menu choose Church settings.' },
          { title: 'Make your changes', detail: 'Edit the church profile, address, setlist emails and which emails you get.' },
          { title: 'Save', detail: 'Save changes at the top lights up once something has changed.' },
        ],
        tryIt: { label: 'Open Church settings', path: '/admin/settings' },
      },
      {
        id: 'profile',
        title: 'Update your profile',
        summary: 'Your name, email and password.',
        steps: [
          { title: 'Open Profile', detail: 'From the account menu choose Profile.' },
          { title: 'Change your email', detail: 'Enter your current password when you change your email, then Save profile.' },
          { title: 'Change your password', detail: 'Choose Change password and enter your current and new passwords.' },
        ],
        tryIt: { label: 'Open Profile', path: '/admin/profile' },
      },
      {
        id: 'api-keys',
        title: 'Create an API key',
        summary: 'Connect another tool to your church data. Read only.',
        steps: [
          { title: 'Open Developer', detail: 'From the account menu choose Developer. API keys need the Basic plan or above.' },
          { title: 'Choose New API key', detail: 'Name it after the integration that will use it.' },
          { title: 'Copy it now', detail: 'The full key is shown once. Store it somewhere safe.' },
          { title: 'Pause or revoke', detail: 'Pause blocks a key for a while; Resume brings it back. Revoke stops it for good.' },
          { title: 'Share the address', detail: 'Requests go to https://api.churchnow.co with the key in the X-ChurchNow-API-Key header.' },
        ],
        tryIt: { label: 'Open Developer', path: '/admin/developer-settings' },
      },
    ],
  },
  {
    id: 'billing',
    title: 'Plan and billing',
    description: 'Your plan, payment method and invoices',
    articles: [
      {
        id: 'change-plan',
        title: 'Change your plan',
        summary: 'Compare plans and switch.',
        steps: [
          { title: 'Open Plan', detail: 'From the account menu choose Plan and billing. Your current plan is outlined.' },
          { title: 'Upgrade or switch', detail: 'Choose Upgrade or Switch on a plan. Moving to a smaller plan shows what changes before anything happens.' },
          { title: 'Enterprise', detail: 'Choose Contact sales and we’ll set it up with you.' },
        ],
        tryIt: { label: 'Open Plan', path: '/admin/subscription' },
      },
      {
        id: 'invoices',
        title: 'Find an invoice or update your card',
        summary: 'Download invoices and manage how you pay.',
        steps: [
          { title: 'Open Billing', detail: 'In Plan and billing choose the Billing section. Invoices are listed newest first.' },
          { title: 'Download a PDF', detail: 'Choose PDF on any invoice.' },
          { title: 'Update your card', detail: 'Choose Update payment method. Your card is managed securely by Stripe.' },
        ],
        tryIt: { label: 'Open Billing', path: '/admin/billing' },
      },
    ],
  },
];
